import { createNoopTask } from '../../schema/task'
import type { ConductorTask } from '../../schema/task'

/** Marks the branch settled once the switch's chosen case has finished. */
const JOIN_MARKER_SUFFIX = '_joined'

/**
 * Prepares branches to be joined on, and names the task each one is joined by.
 *
 * Conductor completes a SWITCH as soon as it picks a case — before the tasks in
 * that case run — so a join waiting on one releases early, and whatever reads
 * the case's output sees nothing written yet. A branch ending in a switch gets
 * a NOOP, which runs after whichever case ran, and the join waits on that.
 *
 *   [.., switch]        -> join fires when the switch picks     (wrong)
 *   [.., switch, noop]  -> join fires when the case has run     (right)
 */
export const closeBranchesForJoin = (
  branches: ConductorTask[][]
): { branches: ConductorTask[][]; joinOn: string[] } => {
  const closed = branches.map(closeBranch)

  return {
    branches: closed,
    joinOn: closed.reduce<string[]>((references, branch) => {
      const last = branch.at(-1)
      return last ? [...references, last.taskReferenceName] : references
    }, [])
  }
}

const closeBranch = (branch: ConductorTask[]): ConductorTask[] => {
  const last = branch.at(-1)
  if (last?.type !== 'SWITCH') return branch

  return [
    ...branch,
    createNoopTask({
      name: 'switch-joined',
      // Derived from the switch it closes, so the name is unique and points at
      // what it is waiting for.
      taskReferenceName: `${last.taskReferenceName}${JOIN_MARKER_SUFFIX}`,
      inputParameters: {}
    })
  ]
}
